import { useEffect, useRef } from 'react';

interface CherryBlossomsProps {
  isDarkMode: boolean;
}

interface Petal {
  x: number;
  y: number;
  size: number;
  velocityY: number;
  velocityX: number;
  rotation: number;
  rotationSpeed: number;
  opacity: number;
  color: string;
}

const CherryBlossoms = ({ isDarkMode }: CherryBlossomsProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const petals: Petal[] = [];
    const numPetals = 50;

    const colors = [
      'rgba(255, 192, 203, ',  // Light pink
      'rgba(255, 182, 193, ',  // Lighter pink
      'rgba(255, 228, 225, ',  // Misty rose
      'rgba(255, 240, 245, ',  // Lavender blush
    ];

    // Initialize petals
    for (let i = 0; i < numPetals; i++) {
      petals.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height - canvas.height,
        size: Math.random() * 8 + 4,
        velocityY: Math.random() * 1 + 0.5,
        velocityX: (Math.random() - 0.5) * 0.8,
        rotation: Math.random() * Math.PI * 2,
        rotationSpeed: (Math.random() - 0.5) * 0.05,
        opacity: Math.random() * 0.5 + 0.4,
        color: colors[Math.floor(Math.random() * colors.length)],
      });
    }

    let animationId: number;

    const draw = () => {
      const { width, height } = canvas;
      ctx.clearRect(0, 0, width, height);

      petals.forEach((petal) => {
        // Update position
        petal.y += petal.velocityY;
        petal.x += petal.velocityX;
        petal.rotation += petal.rotationSpeed;

        // Wrap around edges
        if (petal.y > height + petal.size) {
          petal.y = -petal.size;
          petal.x = Math.random() * width;
        }
        if (petal.x < -petal.size) petal.x = width + petal.size;
        if (petal.x > width + petal.size) petal.x = -petal.size;

        // Draw petal shape (ellipse with rotation)
        ctx.save();
        ctx.translate(petal.x, petal.y);
        ctx.rotate(petal.rotation);

        // Create gradient for petal depth
        const gradient = ctx.createRadialGradient(0, 0, 0, 0, 0, petal.size);
        gradient.addColorStop(0, petal.color + petal.opacity + ')');
        gradient.addColorStop(0.7, petal.color + (petal.opacity * 0.6) + ')');
        gradient.addColorStop(1, petal.color + '0)');

        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.ellipse(0, 0, petal.size, petal.size * 0.6, 0, 0, Math.PI * 2);
        ctx.fill();

        ctx.restore();
      });

      animationId = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener('resize', resize);
    };
  }, [isDarkMode]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        opacity: 0.5,
      }}
    />
  );
};

export default CherryBlossoms;
